import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
  constants,
  CryptoFormatter,
  CurrencyFormatter,
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { casinoRoutes } from '@atlas/routing';
import { Button, Icon, Label } from '@atlas/ui';

import { POSTHOG_DEMO_SOURCE } from 'common/constants';
import { CDN_TYPE_GAME, imageUrlCDN } from 'common/utils';
import { callUserSelfExcludedPopup, normalizePath } from 'components/Casino/utils';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import { useIsRtl } from 'hooks/useIsRtl';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

const { CRYPTO_CURRENCY_CODE } = constants;

const { game: casinoGamePath } = casinoRoutes;

const CasinoLastWinner = ({ lastWinner }) => {
  const { lang } = useSettings();

  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);
  const demoButtonStateForSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.desktop_demo_logged',
    false,
  );
  const demoButtonStateForNonSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.desktop_demo_non_logged',
    false,
  );
  const isRtl = useIsRtl();
  const applicationState = useApplicationState();
  const isAccountBlocked = useAccountBlocked();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const {
    account,
    account: { selfExcludedDueDate } = {},
    authenticated,
    bankAccounts,
  } = applicationState;

  const history = useHistory();

  const {
    game: { iconUrl, id: gameId, isDemoAvailable, name: gameName } = {},
    info: { amount, currency, login } = {},
  } = lastWinner;

  const [showImage, setShowImage] = useState(true);

  const hideImage = () => {
    setShowImage(false);
  };

  const showAccountBlockedPopup = useCallback(() => {
    NotificationManager.display({
      id: 'accountBlocked',
      type: 'accountBlocked',
    });
  }, []);

  const {
    location: { pathname },
    push: historyPush,
  } = useHistory();
  const path = normalizePath(pathname);

  const onSelectGame = useCallback(async () => {
    if (isAccountBlocked) {
      showAccountBlockedPopup();
      return;
    }

    // if user is self excluded
    if (selfExcludedDueDate && dayjs().isBefore(selfExcludedDueDate)) {
      return callUserSelfExcludedPopup();
    }
    const gameUrl = new URL(`${path}/${casinoGamePath}/${gameId}`, location.origin);

    gameUrl.searchParams.append('lastWinner', 'true');
    gameUrl.searchParams.set('from', location.pathname + location.search);

    if (
      authenticated &&
      needsVerification(account, bankAccounts) &&
      isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
    ) {
      return NotificationManager.display({
        data: { history },
        message: 'verificationPopup',
        type: 'verificationPopup',
      });
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    return historyPush(gameUrl.pathname + gameUrl.search);
  }, [
    isAccountBlocked,
    selfExcludedDueDate,
    path,
    gameId,
    authenticated,
    account,
    bankAccounts,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
    historyPush,
    showAccountBlockedPopup,
    history,
  ]);

  const onSelectDemoGame = useCallback(async () => {
    const demoGameUrl = new URL(`${path}/${casinoGamePath}/${gameId}`, location.origin);

    demoGameUrl.searchParams.append('demo', 'true');
    demoGameUrl.searchParams.append('lastWinner', 'true');
    demoGameUrl.searchParams.set('from', location.pathname + location.search);

    if (isAccountBlocked) {
      showAccountBlockedPopup();
      return;
    }

    if (
      authenticated &&
      needsVerification(account, bankAccounts) &&
      isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
    ) {
      return NotificationManager.display({
        data: { history },
        message: 'verificationPopup',
        type: 'verificationPopup',
      });
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    historyPush(demoGameUrl.pathname + demoGameUrl.search, {
      demoSource: POSTHOG_DEMO_SOURCE.LAST_WINNERS_CARD,
    });
  }, [
    path,
    gameId,
    isAccountBlocked,
    authenticated,
    account,
    bankAccounts,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
    historyPush,
    showAccountBlockedPopup,
    history,
  ]);

  const userName = () => {
    if (login.length > 3) return `${login.slice(0, 3)}*`;

    return `${login.slice(0, -2)}***`;
  };

  const formatter = useMemo(
    () =>
      CRYPTO_CURRENCY_CODE.includes(currency)
        ? new CryptoFormatter(languageCode, currency)
        : new CurrencyFormatter(languageCode, currency),
    [currency, languageCode],
  );

  return (
    <div className="casino-winner" dir={isRtl ? 'rtl' : 'ltr'}>
      {iconUrl && showImage ? (
        <img
          alt={gameName}
          className="casino-winner__img"
          loading="lazy"
          onError={hideImage}
          role="presentation"
          src={imageUrlCDN(iconUrl, CDN_TYPE_GAME)}
        />
      ) : (
        <div className="casino-winner__default-img">
          <Icon height={44} name="casino-coin" width={44} />
        </div>
      )}

      <div className="casino-winner__info">
        <Label className="casino-winner__title" message="Congratulations" />
        <div className="casino-winner-text">
          <div className="casino-winner-text__inner">{userName()}</div>
          <div className="casino-winner-text__inner">
            <Label message="won" />
          </div>
          <div className="casino-winner-text__inner">{formatter.format(amount)}</div>
        </div>
        <div className="casino-winner__game">{gameName}</div>
      </div>

      <div className="casino-winner-overlay">
        <div className="casino-winner-overlay__backdrop" />
        <div className="casino-winner-overlay__content">
          {isDemoAvailable &&
            ((authenticated && demoButtonStateForSignedInUsers) ||
              (!authenticated && demoButtonStateForNonSignedInUsers)) && (
              <Button block label="demo" onClick={onSelectDemoGame} size="big" type="secondary" />
            )}
          <Button block label="play" onClick={onSelectGame} size="big" type="primary" />
        </div>
      </div>
    </div>
  );
};

CasinoLastWinner.propTypes = {
  lastWinner: PropTypes.shape({
    game: PropTypes.object,
    info: PropTypes.object,
  }),
};

export default CasinoLastWinner;
